import { memo, ReactNode } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { InvestmentDTO } from '@inversor-hub/types';
import { formatCurrency, formatPercentage } from '@/utils/misc';
import { GlossaryIcon } from '../../shared/GlossaryHelp';

interface ParticipationCardProps {
  investment: InvestmentDTO;
  ownershipPct: number;
}

const Stat = ({
  label,
  help,
  value,
  color,
}: {
  label: string;
  help?: ReactNode;
  value: string;
  color?: string;
}) => (
  <Box
    sx={{
      minWidth: 0,
      display: 'flex',
      flexDirection: { xs: 'row', sm: 'column' },
      alignItems: { xs: 'baseline', sm: 'stretch' },
      justifyContent: 'space-between',
      gap: 1,
    }}
  >
    <Typography variant="overline" color="text.secondary" lineHeight={1.4}>
      {label}
      {help}
    </Typography>
    <Typography variant="h5" fontWeight={700} color={color} sx={{ flexShrink: 0 }}>
      {value}
    </Typography>
  </Box>
);

export const ParticipationCard = memo(({ investment, ownershipPct }: ParticipationCardProps) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      bgcolor: 'primary.50',
      border: '1px solid',
      borderColor: 'primary.100',
    }}
  >
    <Typography variant="overline" color="primary.dark" lineHeight={1.4}>
      Tu participación · {formatPercentage(ownershipPct)}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
      Los importes de abajo son solo tu parte. El resto de la pestaña muestra el activo completo.
    </Typography>

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
        },
        gap: { xs: 1.25, sm: 2.5 },
      }}
    >
      <Stat
        label="Tu inversión"
        help={<GlossaryIcon term="capex" />}
        value={formatCurrency(Math.round(investment.capex.total * ownershipPct))}
      />
      {investment.monthlyRent != null ? (
        <Stat
          label="Renta mensual"
          value={formatCurrency(Math.round(investment.monthlyRent * ownershipPct))}
        />
      ) : null}
      {investment.monthlyCashflow != null ? (
        <Stat
          label="Cashflow mensual"
          help={<GlossaryIcon term="cashflow" />}
          value={formatCurrency(Math.round(investment.monthlyCashflow * ownershipPct))}
          color={investment.monthlyCashflow >= 0 ? 'success.main' : 'error.main'}
        />
      ) : null}
      <Stat
        label="Rentabilidad bruta"
        help={<GlossaryIcon term="grossReturn" />}
        value={formatPercentage(investment.grossReturn)}
        color="success.main"
      />
    </Box>
  </Paper>
));

ParticipationCard.displayName = 'ParticipationCard';
